import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Card, Loading } from '../../../common';
import identityService from '../../../../services/identityService';
import './Charts.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * RiskChart Component
 * Displays REAL-TIME risk analysis as a stacked bar chart
 * Data comes from backend API, not dummy calculations
 */
const RiskChart = ({ stats, loading: statsLoading = false }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRiskData = async () => {
      setLoading(true);
      try {
        const response = await identityService.getRiskBreakdown();
        
        if (response?.success && response?.data) {
          // console.log('✅ Risk breakdown data received:', response.data);
          setChartData(response.data);
        } else {
          console.warn('⚠️ Invalid risk breakdown response');
          setError('Invalid response format');
        }
      } catch (err) {
        console.error('❌ Error fetching risk breakdown:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRiskData();
  }, []);

  // Build chart data from API response
  const buildChartData = () => {
    if (!chartData?.chartData) {
      return null;
    }

    const { labels, datasets } = chartData.chartData;

    return {
      labels: labels,
      datasets: [
        {
          label: 'Low Risk',
          data: datasets.low,
          backgroundColor: '#28a745',
          borderRadius: 4
        },
        {
          label: 'Medium Risk',
          data: datasets.medium,
          backgroundColor: '#ffc107',
          borderRadius: 4
        },
        {
          label: 'High Risk',
          data: datasets.high,
          backgroundColor: '#dc3545',
          borderRadius: 4
        },
        {
          label: 'Critical Risk',
          data: datasets.critical,
          backgroundColor: '#721c24',
          borderRadius: 4
        }
      ]
    };
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif"
          }
        }
      },
      y: {
        stacked: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif"
          },
          stepSize: 1
        }
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 12,
            family: "'Inter', sans-serif"
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(26, 26, 46, 0.9)',
        titleFont: {
          size: 13,
          family: "'Inter', sans-serif"
        },
        bodyFont: {
          size: 12,
          family: "'Inter', sans-serif"
        },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.raw}`;
          }
        }
      }
    }
  };

  const data = buildChartData();
  const isLoading = loading || statsLoading;

  return (
    <Card className="chart-card">
      <div className="chart-header">
        <h3 className="chart-title">Risk Analysis</h3>
        <span className="chart-subtitle">
          By identity type and risk level
        </span>
      </div>
      <div className="chart-body">
        {isLoading ? (
          <div className="chart-loading">
            <Loading size="md" text="Loading risk data..." />
          </div>
        ) : error ? (
          <div className="chart-error">
            <p>Failed to load risk data</p>
            <small>{error}</small>
          </div>
        ) : data ? (
          <div className="chart-container bar-container">
            <Bar data={data} options={options} />
          </div>
        ) : (
          <div className="chart-empty">
            <p>No risk data available</p>
          </div>
        )}
      </div>
      
      {/* Risk Summary */}
      {chartData?.breakdown?.total && (
        <div className="chart-footer">
          <div className="risk-summary">
            <div className="risk-summary-item low">
              <span className="risk-dot"></span>
              <span>Low: {chartData.breakdown.total.low}</span>
            </div>
            <div className="risk-summary-item medium">
              <span className="risk-dot"></span>
              <span>Medium: {chartData.breakdown.total.medium}</span>
            </div>
            <div className="risk-summary-item high">
              <span className="risk-dot"></span>
              <span>High: {chartData.breakdown.total.high}</span>
            </div>
            <div className="risk-summary-item critical">
              <span className="risk-dot"></span>
              <span>Critical: {chartData.breakdown.total.critical}</span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default RiskChart;